import React, { useEffect, useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router"
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux'

import { TOGGLE_LEFT_MENU } from "store/layout/actions";
import { httpOk, GET, closeWebSession } from "helpers/api";
import { clearTemporaryStorage } from "helpers/helpers";

function SidebarContent() {
	const { t } = useTranslation()
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const layoutSelector = useSelector(state => state.layout)
	const reduxRerender = useSelector(state => state.rerender);

	const [menu, setMenu] = useState([])
	const [search, setSearch] = useState('')
	const [badges, setBadges] = useState({})

	async function checkMenuAccess() {
		const response = await GET('/users/navigation')
		if (httpOk(response)) setMenu(openCurrentSection(response.data.data || []))
	}

	// Раскрываем раздел, в котором лежит текущая страница: после F5 или перехода
	// не из меню (избранное, ссылка) иначе все разделы свёрнуты
	function openCurrentSection(items) {
		const current = items.find(itemMenu =>
			Array.isArray(itemMenu.children) &&
			itemMenu.children.some(child => !child.title && isMenuItemActive(child.url))
		)
		if (!current || current.active) return items
		return items.map(itemMenu => ({ ...itemMenu, active: itemMenu === current }))
	}

	// Счётчики незавершённых действий по разделам: { "/report-organizations": 3 }.
	// Показываем бейджем в меню, чтобы было видно, не заходя внутрь.
	async function getNavigationBadges() {
		const response = await GET('/navigation-badges', {}, { notification: false })
		if (httpOk(response)) setBadges(response.data.data || {})
	}

	function badgeByUrl(url = '') {
		if (!url) return 0
		const path = String(url).split('?')[0]
		return badges[path] || 0
	}

	// У свёрнутого раздела показываем сумму бейджей его пунктов
	function badgeByMenu(itemMenu) {
		if (!Array.isArray(itemMenu.children)) return 0
		return itemMenu.children.reduce((sum, child) => sum + badgeByUrl(child.url), 0)
	}

	function isMenuItemActive(url = '') {
		if (!url) return false
		const [path, query = ''] = String(url).split('?')
		if (location.pathname !== path && !location.pathname.startsWith(`${path}/`)) {
			return false
		}
		const current = location.search.startsWith('?') ? location.search.slice(1) : location.search
		// Пункт без параметров на «своём» пути активен только без query, иначе он
		// подсвечивался бы и на соседних пунктах того же пути (/products?product_type_id=2)
		if (!query) return location.pathname !== path || !current
		return current === query || current.startsWith(`${query}&`)
	}

	function matchesSearch(text = '') {
		const query = search.trim().toLowerCase()
		if (!query) return true
		return String(t(text) || text).toLowerCase().includes(query)
	}

	function getFilteredMenu() {
		const query = search.trim().toLowerCase()
		if (!query) {
			return menu?.filter(itemMenu => Array.isArray(itemMenu.children) && itemMenu.children.length > 0) || []
		}

		return (menu || [])
			.map(itemMenu => {
				if (!Array.isArray(itemMenu.children) || itemMenu.children.length === 0) return null

				const parentMatch = matchesSearch(itemMenu.name)
				const children = itemMenu.children.filter(child =>
					!child.title && matchesSearch(child.name)
				)

				if (!parentMatch && children.length === 0) return null

				return {
					...itemMenu,
					active: true,
					children: parentMatch ? itemMenu.children : children,
				}
			})
			.filter(Boolean)
	}

	function setActiveChild(item) {
		var menuCopy = [...menu]

		for (let i = 0; i < menuCopy.length; i++) {
			for (let j = 0; j < menuCopy[i]['children'].length; j++) {
				if (item.url === menuCopy[i]['children'][j]['url']) {
					menuCopy[i]['children'][j]['active'] = true;
				} else {
					menuCopy[i]['children'][j]['active'] = false;
				}
			}
		}

		setMenu(menuCopy)
		if (window.innerWidth <= 600 && layoutSelector.showleftMenu) dispatch(TOGGLE_LEFT_MENU(false));
	}

	function setActiveTab(item) {
		if (search.trim()) return

		var menuCopy = [...menu]
		for (let i = 0; i < menuCopy.length; i++) {
			if (item.name === menuCopy[i]['name']) {
				menuCopy[i]['active'] = !menuCopy[i]['active']
			} else {
				menuCopy[i]['active'] = false
			}
		}

		setMenu(menuCopy)
	}

	async function logout() {
		await closeWebSession()
		dispatch({ type: 'USER_LOGGED_OUT', payload: null })
		clearTemporaryStorage()
		navigate('/auth/login')
	}

	useEffect(() => {
		checkMenuAccess()
	}, [reduxRerender?.navigation]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setMenu(prev => openCurrentSection(prev))
	}, [location.pathname, location.search]) // eslint-disable-line react-hooks/exhaustive-deps

	// Бейджи обновляем по таймеру, при переходе между страницами и после действия,
	// которое могло закрыть задачу (accept/reject оплаты диспатчит SET_RERENDER)
	useEffect(() => {
		getNavigationBadges()
	}, [location.pathname, reduxRerender?.navigation]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const timer = setInterval(getNavigationBadges, 1800000)
		return () => clearInterval(timer)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const filteredMenu = getFilteredMenu()

	return (
		<>
			<div className={"vertical-menu " + (layoutSelector.showleftMenu ? 'd-block' : ' d-none')}>
				<div id="sidebar-menu" className="h-100">
					<div className="second-sidebar">
						<div>
							<div className="sidebar-search">
								<i className="uil uil-search"></i>
								<input
									type="text"
									className="sidebar-search-input"
									placeholder={t('search')}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								{!!search &&
									<button
										type="button"
										className="sidebar-search-clear"
										onClick={() => setSearch('')}
									>
										<i className="uil uil-times"></i>
									</button>
								}
							</div>
							<ul>
								{filteredMenu.map((itemMenu, indexMenu) => (
									<li key={indexMenu}>
										<div className="menu-item-header" onClick={() => setActiveTab(itemMenu)}>
											<span className="menu-text">
												<i className={itemMenu.icon}></i>
												{t(itemMenu.name)}
												{!itemMenu.active && badgeByMenu(itemMenu) > 0 &&
													<span className="badge bg-danger ms-2">{badgeByMenu(itemMenu)}</span>
												}
											</span>
											{itemMenu.active ?
												<i className="arrow-icon uil-angle-up"></i>
												:
												<i className="arrow-icon uil-angle-down"></i>
											}
										</div>
										{itemMenu.active &&
											<ul>
												{itemMenu.children.map((item, index) => (
													<li className={isMenuItemActive(item.url) ? "mm-active" : ""} key={index}>
														{!item.title ?
															<NavLink to={item.url}
																onClick={() => setActiveChild(item)}
																className={() => "d-flex " + (isMenuItemActive(item.url) ? "active" : "")}>
																<i className={item.icon}></i>
																<div className="vertical-menu-div">{t(item.name)}</div>
																{badgeByUrl(item.url) > 0 &&
																	<span className="badge bg-danger ms-auto">{badgeByUrl(item.url)}</span>
																}
															</NavLink>
															:
															<div className="second-sidebar-title">
																<div>{t(item.name)}</div>
															</div>
														}
													</li>
												))}
											</ul>
										}
									</li>
								))}
							</ul>
						</div>
						<button type="button" className="sidebar-logout-button" onClick={logout}>
							<i className="uil uil-sign-out-alt"></i>
							<span>{t('logout')}</span>
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default SidebarContent